import PropTypes from 'prop-types';
import { format } from 'date-fns';
import _ from 'lodash';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  Stack
} from '@mui/material';
import logo from 'assets/images/vivekanand.png';
import { numberToWords, resolvePharmacyInvoiceTotals, adjustGstSummaryForRoundOff, formatPrice } from './utils';
import {
  normalizeHospitalDetails,
  calculateInclusiveLineGst,
  summarizeInclusiveGst,
  getItemGstPercentage,
  getExplicitGstAmounts,
  pickHospitalLogo
} from 'utils/gst';
import { getInvoiceInsurerName } from 'utils/invoiceBillingProvider';
export default function PharmacyInvoiceA4Content({ customerInfo, invoiceData, hospitalData }) {
  const {
    invoice_number,
    created_at,
    payment_method,
    items: { pharmacy = [] } = {},
  } = invoiceData;

  const hospitalDetails = normalizeHospitalDetails(hospitalData || {});
  const hospitalLogo = pickHospitalLogo(hospitalData, logo);
  const summaryMode = invoiceData?.pharmacy_gst_summary?.mode;
  const inferredMode = pharmacy.some(item => Number(item?.cgst || item?.cgst_amount || 0) > 0 || Number(item?.sgst || item?.sgst_amount || 0) > 0)
    ? 'cgst_sgst'
    : 'none';
  const gstMode = summaryMode === 'cgst_sgst' ? 'cgst_sgst' : inferredMode;
  const showGst = gstMode === 'cgst_sgst';
  const insurerName = getInvoiceInsurerName(invoiceData);

  let doctorInfo = null;
  if (invoiceData.other_details) {
    try {
      const additionalInfo = typeof invoiceData.other_details === 'string'
        ? JSON.parse(invoiceData.other_details)
        : invoiceData.other_details;
      if (additionalInfo.doctor_id) {
        doctorInfo = invoiceData.doctor || { id: additionalInfo.doctor_id, full_name: additionalInfo.doctor_name || 'N/A' };
      }
    } catch (e) {
      doctorInfo = null;
    }
  }

  if (invoiceData.doctor && !doctorInfo) {
    doctorInfo = invoiceData.doctor;
  }

  const processedItems = () => {
    if (!pharmacy.length) return { activeItems: [], cancelledItems: [] };

    const hasCancelledItems = pharmacy.some(item => item.status === 'cancelled');
    const hasStatusField = pharmacy.some(item => item.status);

    const normalizeItem = (item) => {
      const unitPrice = parseFloat(item.selling_price ?? item.price ?? 0);
      return {
        ...item,
        price: unitPrice,
        selling_price: unitPrice,
        quantity: Number(item.quantity ?? item.qty ?? item.cancel_quantity ?? 0)
      };
    };

    if (!hasStatusField) {
      return { activeItems: pharmacy.map(normalizeItem), cancelledItems: [] };
    }

    if (hasCancelledItems) {
      const itemsMap = {};
      pharmacy.forEach(rawItem => {
        const item = normalizeItem(rawItem);
        const itemId = item.id;
        if (!itemsMap[itemId]) {
          itemsMap[itemId] = {
            id: itemId,
            name: item.name,
            price: item.price,
            hsn_code: item.hsn_code || '',
            selling_price: item.selling_price,
            activeQuantity: 0,
            cancelledQuantity: 0,
            discount_in_perc: item.discount_in_perc || 0,
            gst_percentage: getItemGstPercentage(item),
            batch_number: item.batch_number,
            expiry_date: item.expiry_date,
            batches: Array.isArray(item.batches) ? [...item.batches] : []
          };
        } else {
          const lineDisc = parseFloat(item.discount_in_perc || 0);
          const prevDisc = parseFloat(itemsMap[itemId].discount_in_perc || 0);
          if (lineDisc > prevDisc) {
            itemsMap[itemId].discount_in_perc = lineDisc;
          }
        }

        if (item.status === 'active') {
          itemsMap[itemId].activeQuantity = Number(item.quantity);
        } else if (item.status === 'cancelled') {
          itemsMap[itemId].cancelledQuantity += Number(item.quantity);
        }

        if (Array.isArray(item.batches) && item.batches.length > 0) {
          itemsMap[itemId].batches = [...(itemsMap[itemId].batches || []), ...item.batches];
        }
      });

      const activeItems = Object.values(itemsMap)
        .map(item => ({
          ...item,
          quantity: item.activeQuantity - item.cancelledQuantity
        }))
        .filter(item => item.quantity > 0);

      return {
        activeItems,
        cancelledItems: pharmacy.filter(item => item.status === 'cancelled').map(normalizeItem)
      };
    }

    return {
      activeItems: pharmacy.filter(item => item.status === 'active').map(normalizeItem),
      cancelledItems: []
    };
  };

  const { activeItems, cancelledItems } = processedItems();

  const computedLineDiscountTotal = activeItems.reduce((sum, item) => {
    const unit = parseFloat(item.selling_price || item.price || 0);
    const qty = Number(item.quantity || 1);
    return sum + (unit * qty * parseFloat(item.discount_in_perc || 0)) / 100;
  }, 0);

  const gstSummary = summarizeInclusiveGst({
    items: activeItems.map(item => ({
      ...item,
      batches: [],
      cgst_amount: 0,
      sgst_amount: 0,
      igst_amount: 0,
      cgst: 0,
      sgst: 0,
      igst: 0
    })),
    gstEnabled: showGst,
    mode: gstMode
  });

  const subtotalBeforeDiscount = Number(invoiceData.actualSubtotal ?? invoiceData.original_amount ?? 0);
  const discountPercent = Number(invoiceData.discount || 0);
  const explicitActualDiscount =
    invoiceData.actualDiscount != null && invoiceData.actualDiscount !== ''
      ? Number(invoiceData.actualDiscount)
      : null;
  const totalDiscountAmount = Number(
    computedLineDiscountTotal > 0
      ? computedLineDiscountTotal
      : (explicitActualDiscount !== null
          ? explicitActualDiscount
          : (subtotalBeforeDiscount > 0 ? (subtotalBeforeDiscount * discountPercent) / 100 : 0))
  );
  const anyDiscount =
    pharmacy.some(item => parseFloat(item.discount_in_perc || 0) > 0) || totalDiscountAmount > 0;
  const cancelledTotal = cancelledItems.reduce((sum, item) => {
    const explicitAmounts = getExplicitGstAmounts(item);
    const lineSummary = calculateInclusiveLineGst({
      grossUnitPrice: Number(item.selling_price || item.price || 0),
      quantity: Number(item.quantity || 1),
      discountPercentage: Number(item.discount_in_perc || 0),
      taxPercentage: getItemGstPercentage(item),
      gstEnabled: showGst,
      mode: gstMode,
      explicitCgstAmount: explicitAmounts.cgstAmount,
      explicitSgstAmount: explicitAmounts.sgstAmount,
      explicitIgstAmount: explicitAmounts.igstAmount
    });
    return sum + lineSummary.totalAmount;
  }, 0);

  const resolvedTotals = resolvePharmacyInvoiceTotals({
    netTotal: gstSummary.totalAmount
  });
  const roundOff = Number(invoiceData.roundOff ?? resolvedTotals.roundOffDisplay);
  const isPositiveRoundOff = typeof invoiceData.isPositiveRoundOff === 'boolean'
    ? invoiceData.isPositiveRoundOff
    : resolvedTotals.isPositiveRoundOff;
  const grandTotal = resolvedTotals.grandTotal;
  const displayGstSummary = adjustGstSummaryForRoundOff(gstSummary, grandTotal) ?? gstSummary;
  const totalInWords = `Rs. ${numberToWords(grandTotal)} Only`;
  const createdAtDate = new Date(created_at || new Date());

  const renderRow = (item, index, rowLabelPrefix = '') => {
    const price = parseFloat(item.selling_price || item.price || 0);
    const qty = parseInt(item.quantity || 1, 10);
    const discount = parseFloat(item.discount_in_perc || 0);
    const gstPercentage = getItemGstPercentage(item);
    const explicitAmounts = getExplicitGstAmounts(item);
    const lineSummary = calculateInclusiveLineGst({
      grossUnitPrice: price,
      quantity: qty,
      discountPercentage: discount,
      taxPercentage: gstPercentage,
      gstEnabled: showGst,
      mode: gstMode,
      explicitCgstAmount: explicitAmounts.cgstAmount,
      explicitSgstAmount: explicitAmounts.sgstAmount,
      explicitIgstAmount: explicitAmounts.igstAmount
    });

    return (
      <TableRow key={`${rowLabelPrefix}${item.id || item.name}-${index}`}>
        <TableCell>{rowLabelPrefix}{index + 1}</TableCell>
        <TableCell>
          {item.name || '-'}
          {(item.salt || item.composition || item.generic_name) && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
              ({item.salt || item.composition || item.generic_name})
            </Typography>
          )}
        </TableCell>
        <TableCell>{item.hsn_code || '-'}</TableCell>
        <TableCell>
          {Array.isArray(item.batches) && item.batches.length > 0
            ? item.batches.map((b, bi) => (
                <Box key={bi} sx={{ whiteSpace: 'nowrap' }}>
                  {b.batch_number || '-'}{' '}({b.expiry_date ? format(new Date(b.expiry_date), 'MM/yy') : '-'})
                </Box>
              ))
            : <>
                {item.batch_number && <Box sx={{ whiteSpace: 'nowrap' }}>{item.batch_number}</Box>}
                {item.expiry_date ? format(new Date(item.expiry_date), 'MM/yy') : '-'}
              </>
          }
        </TableCell>
        <TableCell>{qty}</TableCell>
        <TableCell>{formatPrice(price)}</TableCell>
        {anyDiscount && <TableCell>{discount.toFixed(2)}%</TableCell>}
        {/* {showGst && <TableCell>{gstPercentage.toFixed(2)}%</TableCell>} */}
        {showGst && gstMode === 'cgst_sgst' && (
          <>
            <TableCell>{lineSummary.cgstPercentage.toFixed(2)}%</TableCell>
            <TableCell>{lineSummary.sgstPercentage.toFixed(2)}%</TableCell>
          </>
        )}
        <TableCell>{lineSummary.totalAmount.toFixed(2)}</TableCell>
      </TableRow>
    );
  };

  // Group cancelled items by return event (cancellation_ledger_id)
  const groupedCancelledEvents = (() => {
    if (!cancelledItems || cancelledItems.length === 0) return [];

    const eventsMap = {};
    cancelledItems.forEach(item => {
      const ledgerId = item.cancellation_ledger_id || 'unknown';
      const timeStr = item.created_at ? format(new Date(item.created_at), 'dd-MM-yyyy HH:mm') : 'N/A';
      const staffName = item.cancelled_by_name || 'N/A';

      if (!eventsMap[ledgerId]) {
        eventsMap[ledgerId] = {
          time: timeStr,
          staffName: staffName,
          items: []
        };
      }
      eventsMap[ledgerId].items.push(item);
    });
    return Object.values(eventsMap);
  })();

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Box
          component="img"
          src={hospitalLogo}
          alt="Hospital Logo"
          sx={{
            width: 80,
            height: 80,
            mx: 'auto',
            display: 'block',
            mb: 1
          }}
        />
        <Typography variant="h5" gutterBottom>
          {hospitalDetails.name}
        </Typography>
        {hospitalDetails.address && (
          <Typography variant="body2">{hospitalDetails.address}</Typography>
        )}
        {showGst && hospitalDetails.gstNumber && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Hospital GST:</strong> {hospitalDetails.gstNumber}
            <strong>Hospital PAN:</strong> {hospitalDetails.pan_number || "AAIAS8992G"}
          </Typography>
        )}
      </Box>

      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={6}>
          <Typography align="left"><strong>Patient:</strong> {customerInfo?.full_name} ({customerInfo?.id})</Typography>
          <Typography align="left"><strong>Date & Time:</strong> {format(createdAtDate, 'dd-MM-yyyy HH:mm')}</Typography>
          {insurerName && <Typography align="left"><strong>Insurance:</strong> {insurerName}</Typography>}
          <Typography align="left"><strong>Receptionist:</strong> {invoiceData?.receptionist?.full_name} ({invoiceData?.receptionist?.id})</Typography>
          {doctorInfo && (
            <Typography align="left"><strong>Doctor:</strong> {doctorInfo.full_name} ({doctorInfo.id})</Typography>
          )}
        </Grid>
        <Grid item xs={6}>
          <Typography align="right"><strong>Bill No:</strong> {invoice_number}</Typography>
          <Typography align="right"><strong>Payment Method:</strong> {payment_method === 'hybrid' ? 'Cash + Online' : (_.capitalize(payment_method) || 'N/A')}</Typography>
          <Typography align="right"><strong>Store:</strong> {invoiceData?.store_name || hospitalDetails.name}</Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {activeItems.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Sr.</strong></TableCell>
                <TableCell><strong>Product Description</strong></TableCell>
                <TableCell><strong>HSN</strong></TableCell>
                <TableCell><strong>Batch / Exp</strong></TableCell>
                <TableCell><strong>QTY.</strong></TableCell>
                <TableCell><strong>M.R.P.</strong></TableCell>
                {anyDiscount && <TableCell><strong>Disc %</strong></TableCell>}
                {/* {showGst && <TableCell><strong>GST %</strong></TableCell>} */}
                {showGst && gstMode === 'cgst_sgst' && (
                  <>
                    <TableCell><strong>CGST %</strong></TableCell>
                    <TableCell><strong>SGST %</strong></TableCell>
                  </>
                )}
                <TableCell><strong>Amount</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeItems.map((item, idx) => renderRow(item, idx))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {groupedCancelledEvents.length > 0 && (() => {
        const grandCancelTotal = groupedCancelledEvents.reduce((gsum, evt) =>
          gsum + evt.items.reduce((sum, item) => {
            const price = parseFloat(item.selling_price || item.price || 0);
            const qty = parseInt(item.quantity || 1, 10);
            const discount = parseFloat(item.discount_in_perc || 0);
            const explicitAmounts = getExplicitGstAmounts(item);
            const lineSummary = calculateInclusiveLineGst({
              grossUnitPrice: price, quantity: qty, discountPercentage: discount,
              taxPercentage: getItemGstPercentage(item), gstEnabled: showGst, mode: gstMode,
              explicitCgstAmount: explicitAmounts.cgstAmount,
              explicitSgstAmount: explicitAmounts.sgstAmount,
              explicitIgstAmount: explicitAmounts.igstAmount
            });
            return sum + lineSummary.totalAmount;
          }, 0), 0);

        const colCount = 5 + (anyDiscount ? 1 : 0) + (showGst && gstMode === 'cgst_sgst' ? 2 : 0);

        return (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 2, mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                Sales Returns ({groupedCancelledEvents.length} event{groupedCancelledEvents.length > 1 ? 's' : ''})
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                Total Returned: Rs {grandCancelTotal.toFixed(2)}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-root': { py: '4px' } }}>
                    <TableCell><strong>Sr.</strong></TableCell>
                    <TableCell><strong>Product Description</strong></TableCell>
                    <TableCell><strong>HSN</strong></TableCell>
                    <TableCell><strong>Batch / Exp</strong></TableCell>
                    <TableCell><strong>QTY.</strong></TableCell>
                    <TableCell><strong>M.R.P.</strong></TableCell>
                    {anyDiscount && <TableCell><strong>Disc %</strong></TableCell>}
                    {showGst && gstMode === 'cgst_sgst' && (
                      <>
                        <TableCell><strong>CGST %</strong></TableCell>
                        <TableCell><strong>SGST %</strong></TableCell>
                      </>
                    )}
                    <TableCell><strong>Amount</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedCancelledEvents.map((evt, evtIdx) => {
                    const eventTotal = evt.items.reduce((sum, item) => {
                      const price = parseFloat(item.selling_price || item.price || 0);
                      const qty = parseInt(item.quantity || 1, 10);
                      const discount = parseFloat(item.discount_in_perc || 0);
                      const explicitAmounts = getExplicitGstAmounts(item);
                      const lineSummary = calculateInclusiveLineGst({
                        grossUnitPrice: price, quantity: qty, discountPercentage: discount,
                        taxPercentage: getItemGstPercentage(item), gstEnabled: showGst, mode: gstMode,
                        explicitCgstAmount: explicitAmounts.cgstAmount,
                        explicitSgstAmount: explicitAmounts.sgstAmount,
                        explicitIgstAmount: explicitAmounts.igstAmount
                      });
                      return sum + lineSummary.totalAmount;
                    }, 0);

                    return (
                      <>
                        {/* Lightweight event header row — no box, no outer table */}
                        <TableRow key={`evt-hdr-${evtIdx}`} sx={{ backgroundColor: '#fef2f2' }}>
                          <TableCell colSpan={Math.ceil(colCount / 2)} sx={{ py: '3px', fontWeight: 'bold', fontSize: '0.75rem', color: 'error.main' }}>
                            Return #{evtIdx + 1} — {evt.time}
                          </TableCell>
                            <TableCell colSpan={Math.floor(colCount / 2) + 1} align="right" sx={{ py: '3px', fontSize: '0.75rem', color: 'text.secondary' }}>
                            By: {evt.staffName || 'N/A'}
                          </TableCell>
                        </TableRow>
                        {evt.items.map((item, idx) => renderRow(item, idx, `R${evtIdx + 1}.`))}
                        {/* Per-event subtotal only when multiple events */}
                        {groupedCancelledEvents.length > 1 && (
                          <TableRow key={`evt-total-${evtIdx}`} sx={{ backgroundColor: '#fff5f5' }}>
                            <TableCell colSpan={colCount} align="right" sx={{ py: '2px', fontSize: '0.75rem', fontStyle: 'italic' }}>
                              Return #{evtIdx + 1} subtotal: <strong>Rs {eventTotal.toFixed(2)}</strong>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                  {/* Grand total row */}
                  <TableRow sx={{ borderTop: '2px solid black', backgroundColor: '#fff0f0' }}>
                    <TableCell colSpan={colCount} align="right" sx={{ fontWeight: 'bold', py: '4px' }}>
                      Total Returned: Rs {grandCancelTotal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      })()}


      <Divider sx={{ my: 2 }} />

      <Grid container justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={5}>
          <Stack spacing={0.5}>

            {/* Subtotal */}
            <Stack 
              direction="row" 
              justifyContent="space-between" 
              sx={{ 
                py: 0.5, 
                px: 1, 
                backgroundColor: '#f5f5f5', 
                borderTop: '1px solid black', 
                borderBottom: '1px solid black',
                fontWeight: 'bold'
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>Subtotal (Before Discount)</Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                Rs {subtotalBeforeDiscount.toFixed(2)}
              </Typography>
            </Stack>

            {/* Discount */}
            {anyDiscount && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Discount</Typography>
                <Typography variant="body2">
                  - Rs {totalDiscountAmount.toFixed(2)}
                </Typography>
              </Stack>
            )}

            {/* Cancelled */}
            {cancelledItems.length > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Cancelled Items</Typography>
                <Typography variant="body2">
                  Rs {cancelledTotal.toFixed(2)}
                </Typography>
              </Stack>
            )}

            {/* Round Off */}
            {roundOff > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Round Down</Typography>
                <Typography variant="body2" color="info.main">
                  {isPositiveRoundOff ? '+' : '-'} Rs {roundOff.toFixed(2)}
                </Typography>
              </Stack>
            )}

            <Divider sx={{ my: 0.5 }} />

            {/* Grand Total FIRST */}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={600}>
                Grand Total
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                Rs {grandTotal.toFixed(2)}
              </Typography>
            </Stack>

            {/* Faint breakdown AFTER total */}
            {showGst && (
              <Stack spacing={0.3} alignItems="flex-start">
                
                {/* Taxable */}
                <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary">
                    Taxable Value
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 90 }}>
                    Rs {displayGstSummary.taxableValue.toFixed(2)}
                  </Typography>
                </Stack>

                {/* Included Tax */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ width: '100%' }}>
                  
                  <Stack spacing={0} sx={{ textAlign: 'left' }}>
                    <Typography variant="caption" color="text.secondary">
                      Included Tax
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1.2, textAlign: 'left' }}
                    >
                      {`CGST: Rs ${displayGstSummary.cgstAmount.toFixed(2)}, SGST: Rs ${displayGstSummary.sgstAmount.toFixed(2)}`}
                    </Typography>
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    Rs {displayGstSummary.totalTax.toFixed(2)}
                  </Typography>

                </Stack>
              </Stack>
            )}

          </Stack>
        </Grid>
      </Grid>

      <Typography variant="body2" sx={{ mt: 2 }}>
        {totalInWords}
      </Typography>
    </Box>
  );
}

PharmacyInvoiceA4Content.propTypes = {
  customerInfo: PropTypes.object.isRequired,
  invoiceData: PropTypes.object.isRequired
};
